import { Box, Stack } from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"
import { CRIT_COLOR, formatClock, LEVEL_COLORS, WARN_COLOR } from "./temperatureDemoFormat"
import {
  CRITICAL_MAX,
  CRITICAL_MIN,
  TARGET_MAX,
  TARGET_MIN,
  TemperatureSeries,
} from "./temperatureDemoGenerator"

/** Visible Y window (°C), as the mockup: values outside are clamped. */
const Y_MIN = -2
const Y_MAX = 14
const clampY = (v: number) => Math.max(Y_MIN, Math.min(Y_MAX, v))

const BANDS: { y1: number; y2: number; fill: string }[] = [
  { y1: CRITICAL_MAX, y2: Y_MAX, fill: "rgba(179,38,30,.07)" },
  { y1: TARGET_MAX, y2: CRITICAL_MAX, fill: "rgba(224,134,0,.09)" },
  { y1: TARGET_MIN, y2: TARGET_MAX, fill: "rgba(67,160,71,.07)" },
  { y1: CRITICAL_MIN, y2: TARGET_MIN, fill: "rgba(224,134,0,.09)" },
  { y1: Y_MIN, y2: CRITICAL_MIN, fill: "rgba(179,38,30,.07)" },
]

const THRESHOLDS: { y: number; stroke: string }[] = [
  { y: CRITICAL_MAX, stroke: CRIT_COLOR },
  { y: TARGET_MAX, stroke: WARN_COLOR },
  { y: TARGET_MIN, stroke: WARN_COLOR },
  { y: CRITICAL_MIN, stroke: CRIT_COLOR },
]

const KEY_SWATCHES = [
  { key: "temperatureDemo.bandTarget", bg: "rgba(67,160,71,.25)" },
  { key: "temperatureDemo.bandWarning", bg: "rgba(224,134,0,.35)" },
  { key: "temperatureDemo.bandCritical", bg: "rgba(179,38,30,.35)" },
]

type Props = {
  series: TemperatureSeries
  now: Date
  ariaLabel: string
}

/**
 * LOCKER-534 — 2–8 °C cold-chain chart: threshold bands, dashed limits,
 * shaded breach spans, the reading line and a dot on the current value.
 * x = minutes relative to now (−span … 0). No animation, so nothing moves
 * for users who asked for reduced motion.
 */
export const TemperatureDemoChart: FC<Props> = ({ series, now, ariaLabel }) => {
  const { t, i18n } = useTranslation()
  const span = series.spanMinutes
  const withDay = series.range === "7d"

  const data = useMemo(
    () => series.points.map((p) => ({ x: -p.minutesAgo, v: clampY(p.value) })),
    [series],
  )
  const ticks = [0, 1, 2, 3, 4].map((k) => -span * (1 - k / 4))
  const tickLabel = (x: number) =>
    x === 0 ? t("temperatureDemo.now") : formatClock(now, -x, i18n.language, withDay)

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,.08)",
        borderRadius: 3,
        p: "12px 12px 8px",
      }}
    >
      <Box role="img" aria-label={ariaLabel} sx={{ width: "100%", height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -24 }}>
            {BANDS.map((b) => (
              <ReferenceArea
                key={`band-${b.y1}`}
                y1={b.y1}
                y2={b.y2}
                fill={b.fill}
                fillOpacity={1}
                ifOverflow="hidden"
              />
            ))}
            {series.breaches.map((br) => (
              <ReferenceArea
                key={`breach-${br.spanFromMinutesAgo}`}
                x1={-br.spanFromMinutesAgo}
                x2={-br.spanToMinutesAgo}
                fill={br.critical ? "rgba(179,38,30,.18)" : "rgba(224,134,0,.18)"}
                fillOpacity={1}
                ifOverflow="hidden"
              />
            ))}
            {THRESHOLDS.map((th) => (
              <ReferenceLine
                key={`th-${th.y}`}
                y={th.y}
                stroke={th.stroke}
                strokeDasharray="4 3"
              />
            ))}
            <XAxis
              type="number"
              dataKey="x"
              domain={[-span, 0]}
              ticks={ticks}
              tickFormatter={tickLabel}
              tick={{ fontSize: 10.5, fill: "#5f6368" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="number"
              domain={[Y_MIN, Y_MAX]}
              ticks={[CRITICAL_MIN, TARGET_MIN, TARGET_MAX, CRITICAL_MAX]}
              tickFormatter={(v: number) => `${v}°`}
              tick={{ fontSize: 10, fill: "#5f6368" }}
              tickLine={false}
              axisLine={false}
              allowDataOverflow
            />
            <Line
              type="linear"
              dataKey="v"
              stroke="#234285"
              strokeWidth={1.75}
              dot={false}
              isAnimationActive={false}
            />
            <ReferenceDot
              x={0}
              y={clampY(series.current)}
              r={4.5}
              fill={LEVEL_COLORS[series.currentLevel].text}
              stroke="#ffffff"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        columnGap={1.75}
        rowGap={0.75}
        sx={{ pt: 1, pl: 1, fontSize: 11, color: "#5f6368" }}
      >
        {KEY_SWATCHES.map((s) => (
          <Stack key={s.key} direction="row" alignItems="center" spacing={0.625}>
            <Box
              component="span"
              aria-hidden
              sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: s.bg }}
            />
            <span>{t(s.key)}</span>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}
